import { compose } from '@doltech/utils/lib/compose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../../hooks/useDeviceQuery.hook';
import { useDolEditorStore } from '../../../../DolEditor/dol-editor.store';
import { DeleteElementButton } from '../../../shared-components/DeleteElementButton';
import { removeNodeByElementId } from '../../shared-transforms';
import { BaseCardLogicProps } from '../CardLogicPlugin.types';
import { useCardLogicStore, withCardLogicStore } from '../model/card-logic.store';
import { ELEMENT_BY_ID_ATTRIBUTES } from '../model/constants';

interface CardLogicElementProps extends BaseCardLogicProps {}

const Main = styled.div`
  position: relative;
  margin-top: 12px;
  margin-bottom: 12px;

  .wrapper {
    display: grid;
    gap: 24px;
    grid-template-columns: minmax(0, 1fr);

    @media ${deviceBreakpoints.fromTablet} {
      gap: 12px;
      grid-auto-flow: column;
      grid-auto-columns: minmax(0, 1fr);
    }
  }
`;

const CardLogicElementEditableBase = (props: CardLogicElementProps) => {
  const { children, attributes, element, isReadOnly } = props;
  const editor = useSlateStatic();
  const dolEditorStore = useDolEditorStore();
  const cardLogicStore = useCardLogicStore();

  React.useEffect(() => {
    cardLogicStore.setId(element.id);
  }, [cardLogicStore, element.id]);

  React.useEffect(() => {
    cardLogicStore.retrieveTableInfo({ editor });
  }, [dolEditorStore.afterSelectionId, cardLogicStore, cardLogicStore.id, editor]);

  return (
    <Main
      {...{ [ELEMENT_BY_ID_ATTRIBUTES]: element.id }}
      className={cl('card-logic-element', { 'read-only': isReadOnly })}
    >
      <div {...attributes} className="wrapper">
        {children}
      </div>
      {isReadOnly ? null : (
        <DeleteElementButton
          onMouseEnter={cardLogicStore.hoverDeleteButton}
          onMouseLeave={cardLogicStore.stopHoverDeleteButton}
          onClick={() => {
            removeNodeByElementId({
              editor,
              elementId: element.id,
            });
          }}
          visible={cardLogicStore.isInCardLogic}
        />
      )}
    </Main>
  );
};

export const CardLogicElementEditable = compose(
  withCardLogicStore,
  observer
)(CardLogicElementEditableBase);

export const CardLogicElementReadOnly = (props: CardLogicElementProps) => {
  const { children, attributes } = props;

  return (
    <Main className={cl('card-logic-element', 'read-only')}>
      <div {...attributes} className="wrapper">
        {children}
      </div>
    </Main>
  );
};
